<template>
	<div>
		<!-- Header -->
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<button class="btn btn-refresh" @click="goBack" title="Back to policies">
							<i class="fa fa-arrow-left"></i>
						</button>
						{{ isNew ? 'New policy' : form.name || 'Edit policy' }}
						<small class="text-muted d-block" style="margin-left:44px">
							{{
								overrideCount === 0
									? 'Inherits every company setting'
									: 'Overrides ' + overrideCount + ' rule group' + (overrideCount === 1 ? '' : 's')
							}}
						</small>
					</h3>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-default" @click="goBack">Cancel</button>
					<button class="btn btn-primary" :disabled="saving" @click="save">
						<i class="fa fa-check"></i> Save policy
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12 text-center" v-if="loading && !isNew" style="margin-top:40px">
			<img src="../../assets/loading.gif" style="width:48px" alt="Loading" />
		</div>

		<div class="row" v-else style="margin:0">
			<!-- ============ FORM ============ -->
			<div class="col-md-8">
				<!-- Presets -->
				<div class="panel">
					<div class="panel-body">
						<p class="font-weight-bold">Start from a common setup</p>
						<p class="text-muted">
							Fills the form below so you can adjust it. Nothing is saved until you press Save.
						</p>
						<div class="row">
							<div class="col-md-4" v-for="preset in presets" :key="preset.key">
								<div class="preset-box">
									<i :class="preset.icon" class="text-primary"></i>
									<p class="font-weight-bold" style="margin:4px 0 0">{{ preset.label }}</p>
									<p class="text-muted" style="margin:0">{{ preset.blurb }}</p>
									<ul v-if="expandedPreset === preset.key" style="padding-left:18px;margin-top:6px">
										<li v-for="(d, i) in preset.detail" :key="i" class="text-muted">{{ d }}</li>
									</ul>
									<div style="margin-top:8px">
										<button class="btn btn-sm btn-primary" @click="applyPreset(preset)">Use</button>
										<button
											class="btn btn-sm btn-link"
											@click="expandedPreset = expandedPreset === preset.key ? null : preset.key"
										>
											{{ expandedPreset === preset.key ? 'Less' : 'Details' }}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Identity -->
				<div class="panel" @focusin="activeHelp = 'identity'">
					<div class="panel-body">
						<p class="font-weight-bold">Name</p>
						<div class="row">
							<div class="col-md-4 form-group">
								<label>Code</label>
								<input type="text" class="form-control form-cascade-control" placeholder="FACTORY" v-model="form.code" />
							</div>
							<div class="col-md-8 form-group">
								<label>Name</label>
								<input type="text" class="form-control form-cascade-control" placeholder="Factory Floor" v-model="form.name" />
							</div>
						</div>
						<div class="form-group">
							<label>Description</label>
							<textarea
								class="form-control form-cascade-control"
								rows="2"
								placeholder="Who this applies to and why — e.g. Plant workers on the 3-shift rota."
								v-model="form.description"
							></textarea>
						</div>
					</div>
				</div>

				<!-- Shift -->
				<div class="panel" @focusin="activeHelp = 'shift'" @mouseenter="activeHelp = 'shift'">
					<div class="panel-body">
						<p class="font-weight-bold">Working hours</p>
						<div class="row">
							<div class="col-md-6 form-group">
								<label>Shift mode</label>
								<select class="form-control form-cascade-control" v-model="form.shift_mode">
									<option v-for="o in shiftModes" :key="String(o.value)" :value="o.value">{{ o.label }}</option>
								</select>
							</div>
							<div class="col-md-6 form-group">
								<label>Which shift</label>
								<select
									class="form-control form-cascade-control"
									v-model="form.shift_id"
									:disabled="form.shift_mode !== 'fixed'"
								>
									<option :value="null">None</option>
									<option v-for="s in shifts" :key="s.id" :value="s.id">
										{{ s.name }} ({{ s.start_time.slice(0, 5) }}–{{ s.end_time.slice(0, 5) }})
									</option>
								</select>
								<small class="text-muted">
									{{
										form.shift_mode === 'fixed'
											? 'Leave empty to use each employee\'s own shift.'
											: 'Only used with a fixed shift.'
									}}
								</small>
							</div>
						</div>
					</div>
				</div>

				<!-- Capture -->
				<div class="panel" @focusin="activeHelp = 'capture'" @mouseenter="activeHelp = 'capture'">
					<div class="panel-body">
						<p class="font-weight-bold">How they mark attendance</p>
						<div class="form-group">
							<label>Photo at punch</label>
							<select class="form-control form-cascade-control" v-model="form.selfie">
								<option v-for="o in selfieModes" :key="String(o.value)" :value="o.value">{{ o.label }}</option>
							</select>
							<small class="text-muted">
								Choose "No photo" for location-only attendance — the camera never opens and nothing is
								uploaded.
							</small>
						</div>
						<div class="form-group">
							<label>Office QR scan</label>
							<select class="form-control form-cascade-control" v-model="form.qr">
								<option v-for="o in qrModes" :key="String(o.value)" :value="o.value">{{ o.label }}</option>
							</select>
							<small class="text-muted">
								Staff scan the QR shown at each site. Independent of the fence — the code proves which
								office, the fence proves they are there. Print codes under Locations.
							</small>
						</div>
					</div>
				</div>

				<!-- Location -->
				<div class="panel" @focusin="activeHelp = 'geo'" @mouseenter="activeHelp = 'geo'">
					<div class="panel-body">
						<p class="font-weight-bold">Where they can punch</p>
						<div class="form-group">
							<label>Punching away from an allowed site</label>
							<select class="form-control form-cascade-control" v-model="form.geo_outside_action">
								<option v-for="o in outsideActions" :key="String(o.value)" :value="o.value">{{ o.label }}</option>
							</select>
						</div>
						<div class="form-group col-md-6" style="padding-left:0">
							<label>Allowed distance (m)</label>
							<input type="number" min="20" max="20000" class="form-control form-cascade-control" v-model.number="form.geo_radius" />
							<small class="text-muted">Leave empty to use the company radius.</small>
						</div>
					</div>
				</div>

				<!-- Day rules -->
				<div class="panel" @focusin="activeHelp = 'day'" @mouseenter="activeHelp = 'day'">
					<div class="panel-body">
						<p class="font-weight-bold">Late marks and day length</p>
						<div class="row">
							<div class="col-md-6 form-group">
								<label>Late marking</label>
								<select class="form-control form-cascade-control" v-model="form.late_mode">
									<option v-for="o in lateModes" :key="o.value" :value="o.value">{{ o.label }}</option>
								</select>
								<input
									v-if="form.late_mode === 'custom'"
									type="number"
									min="0"
									max="240"
									placeholder="10"
									class="form-control form-cascade-control"
									style="margin-top:6px"
									v-model.number="form.late_after_min"
								/>
							</div>
							<div class="col-md-6 form-group">
								<label>Hours for a full day</label>
								<input type="number" min="0" max="24" step="0.01" class="form-control form-cascade-control" v-model.number="form.min_hours_full" />
								<small class="text-muted">Counted after the unpaid break.</small>
							</div>
						</div>
					</div>
				</div>

				<!-- Overtime -->
				<div class="panel" @focusin="activeHelp = 'overtime'" @mouseenter="activeHelp = 'overtime'">
					<div class="panel-body">
						<p class="font-weight-bold">Overtime</p>
						<div class="form-group col-md-8" style="padding-left:0">
							<select class="form-control form-cascade-control" v-model="form.overtime_enabled">
								<option v-for="o in triState" :key="String(o.value)" :value="o.value">{{ o.label }}</option>
							</select>
						</div>
					</div>
				</div>

				<!-- Status -->
				<div class="panel" @focusin="activeHelp = 'status'" @mouseenter="activeHelp = 'status'">
					<div class="panel-body">
						<p class="font-weight-bold">Availability</p>
						<div class="checkbox">
							<label>
								<input type="checkbox" v-model="form.is_default" /> Default for newly enrolled employees
							</label>
						</div>
						<div class="checkbox">
							<label><input type="checkbox" v-model="form.is_active" /> Active</label>
						</div>
					</div>
				</div>
			</div>

			<!-- ============ HELP, ALONGSIDE ============ -->
			<div class="col-md-4">
				<!-- Live read-back -->
				<div class="panel readback-panel">
					<div class="panel-body">
						<p class="font-weight-bold"><i class="fa fa-eye"></i> What this policy will do</p>
						<ul style="padding-left:18px">
							<li v-for="(line, i) in plainSummary" :key="i">{{ line }}</li>
						</ul>
						<p class="text-muted" style="margin-top:8px">
							Everything not listed follows the company settings.
						</p>
					</div>
				</div>

				<!-- Contextual help for the section in focus -->
				<div class="panel">
					<div class="panel-body">
						<p class="font-weight-bold"><i class="fa fa-info-circle text-muted"></i> {{ help.title }}</p>
						<p v-for="(para, i) in help.body" :key="i" class="text-muted">{{ para }}</p>
					</div>
				</div>

				<!-- The reminder people most often miss -->
				<div class="alert alert-warning">
					Saving changes the rules from now on. To apply them to days that were already scored, run
					<strong>Recompute</strong> on the daily register.
				</div>

				<div class="panel">
					<div class="panel-body">
						<p class="font-weight-bold">How settings combine</p>
						<div class="combine-tree text-muted">
							<div>company settings</div>
							<div style="padding-left:8px">↓ this policy overrides some</div>
							<div style="padding-left:16px">↓ an employee can override again</div>
						</div>
						<p class="text-muted" style="margin-top:8px">
							Which is why a half-filled policy is normal — leave everything you do not need to change
							on "Inherit".
						</p>
						<span class="badge bg-secondary">
							{{ overrideCount === 0 ? 'Overrides nothing yet' : 'Overrides ' + overrideCount + ' group(s)' }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { policyService, shiftService, errorMessage } from './attendanceService'

export default {
	name: 'AttendancePolicyEdit',
	data() {
		return {
			shifts: [],
			policies: [],
			loading: false,
			loaded: false,
			saving: false,
			form: this.toForm(null),
			activeHelp: 'intro',
			expandedPreset: null,
			presets: [
				{
					key: 'office',
					label: 'Office staff',
					icon: 'fa fa-building',
					blurb: 'Fixed hours at one site, no overtime.',
					detail: [
						'Same start and finish every day',
						'Must be at the office to punch, but a manager can approve exceptions',
						'10 minutes of grace before a late mark',
						'Overtime not tracked — salaried staff',
						'Location only, no photo',
					],
					values: {
						code: 'OFFICE',
						selfie: 'none',
						name: 'Office Staff',
						description: 'Fixed shift at the office. Geofence enforced, no overtime.',
						shift_mode: 'fixed',
						geo_outside_action: 'allow_needs_approval',
						geo_radius: 200,
						late_mode: 'custom',
						late_after_min: 10,
						min_hours_full: 8,
						overtime_enabled: false,
					},
				},
				{
					key: 'factory',
					label: 'Factory floor',
					icon: 'fa fa-cog',
					blurb: 'Rotating shifts, strict fence, overtime paid.',
					detail: [
						'Hours come from the rota — mornings one week, nights the next',
						'Punching away from the plant is refused outright',
						'Overtime tracked and paid at the configured rates',
						'A wider fence, because plants are large',
						'Location only, no photo',
					],
					values: {
						code: 'FACTORY',
						selfie: 'none',
						name: 'Factory Floor',
						description:
							'Rotational roster. Punching away from the plant is blocked; overtime is tracked.',
						shift_mode: 'roster',
						geo_outside_action: 'block',
						geo_radius: 300,
						late_mode: 'custom',
						late_after_min: 10,
						min_hours_full: 8,
						overtime_enabled: true,
					},
				},
				{
					key: 'field',
					label: 'Field / sales',
					icon: 'fa fa-map',
					blurb: 'No fixed office, no shift, location only recorded.',
					detail: [
						'No shift, so nobody is ever "late"',
						'Punch from a customer site — location is recorded, never enforced',
						'A shorter day counts as full, since travel eats the hours',
						'Overtime not tracked',
						'Location only, no photo',
					],
					values: {
						code: 'FIELD',
						selfie: 'none',
						name: 'Field Sales',
						description: 'No shift. Punch from anywhere; location is recorded, never enforced.',
						shift_mode: 'none',
						geo_outside_action: 'allow_flagged',
						geo_radius: null,
						late_mode: 'off',
						late_after_min: null,
						min_hours_full: 6,
						overtime_enabled: false,
					},
				},
			],
			selfieModes: [
				{ label: 'Inherit from company', value: null },
				{ label: 'No photo — location only', value: 'none' },
				{ label: 'Selfie when punching in', value: 'in' },
				{ label: 'Selfie when punching out', value: 'out' },
				{ label: 'Selfie on both punches', value: 'both' },
			],
			qrModes: [
				{ label: 'Inherit from company', value: null },
				{ label: 'Off — no QR scan', value: 'none' },
				{ label: 'Scan QR when punching in', value: 'in' },
				{ label: 'Scan QR when punching out', value: 'out' },
				{ label: 'Scan QR on both punches', value: 'both' },
			],
			shiftModes: [
				{ label: 'Inherit from company', value: null },
				{ label: 'No shift — any hours count', value: 'none' },
				{ label: 'Fixed shift — same hours every day', value: 'fixed' },
				{ label: 'Rotational roster — hours vary by day', value: 'roster' },
				{ label: 'Flexible — any time, if the hours add up', value: 'flexi' },
			],
			outsideActions: [
				{ label: 'Inherit from company', value: null },
				{ label: 'Block — refuse the punch', value: 'block' },
				{ label: "Allow, needs a manager's approval", value: 'allow_needs_approval' },
				{ label: 'Allow, just flag it for review', value: 'allow_flagged' },
			],
			lateModes: [
				{ label: 'Inherit from company', value: 'inherit' },
				{ label: 'Never mark anyone late', value: 'off' },
				{ label: 'Late after a grace period', value: 'custom' },
			],
			triState: [
				{ label: 'Inherit from company', value: null },
				{ label: 'Track overtime', value: true },
				{ label: 'Do not track overtime', value: false },
			],
			helpTopics: {
				intro: {
					title: 'What a policy is',
					body: [
						'A named set of attendance rules you attach to a group of people.',
						'One company can run several at once — the factory floor, the office and the sales team all need different rules.',
						'You only fill in what differs from the company settings. Anything left on "Inherit" follows the company, and changing it there later applies here too.',
					],
				},
				identity: {
					title: 'Naming it',
					body: [
						'The code is short and internal (FACTORY, OFFICE). The name is what people read in lists and reports.',
						'Write the description for whoever inherits this after you — say who it applies to and why, not what the settings are.',
					],
				},
				shift: {
					title: 'Shift mode',
					body: [
						'Fixed — everyone works the same hours every day. Pick the shift alongside.',
						'Rotational roster — hours change day to day from an assigned rota. Use this for morning/night rotations.',
						'No shift — for field staff with no set start time. Nobody can be "late" without a shift to be late against.',
						'Flexible — any time of day counts, as long as the hours add up.',
					],
				},
				capture: {
					title: 'Photo & QR at punch',
					body: [
						'A selfie is proof that the right person was there, not just their phone. It matters most where someone could hand their handset to a colleague.',
						'Set "No photo" for location-only attendance — nothing is uploaded and the punch is judged on position alone.',
						'Requiring a photo needs file storage configured on the backend. If uploads are not set up, choose "No photo", otherwise punching will fail at the upload step.',
						'Office QR scan makes staff scan the code shown at each site to punch. It is independent of the geofence — the code proves which office, the fence proves they are physically there, so turn both on for a real office and leave QR off for field staff.',
						'Both settings also exist company-wide under Settings → Capture. Setting them here overrides that, for this group only.',
					],
				},
				geo: {
					title: 'Location rules',
					body: [
						'Block refuses the punch outright — right for a factory gate where there is no legitimate reason to be elsewhere.',
						'Needs approval lets them punch but holds it for a manager. The safe default for office staff who occasionally work off-site.',
						'Flag always counts the punch and just marks the row for review. Right for field staff, where location is evidence rather than a gate.',
						'On distance: 200m suits one building, a large plant needs more. Phone GPS is rarely better than about 20m, so anything tighter than 100m will produce false rejections.',
					],
				},
				day: {
					title: 'Late marks and day length',
					body: [
						'Grace is measured from the shift start. With 10 minutes on a 9:00 shift, arriving at 9:07 is fine and 9:11 is a late mark.',
						'Full-day hours are counted after the unpaid break is deducted. A shift running 22:00–06:00 with a 45-minute break only provides 7.25h — asking for 8 there means every night scores as a half day.',
						'How many late marks add up to a penalty is set once for the company, in Settings → Day rules.',
					],
				},
				overtime: {
					title: 'Overtime',
					body: [
						'Turn it on where extra hours are genuinely paid or banked as comp-off — usually factory and shift work, rarely salaried office staff.',
						'The rates, rounding and daily caps are company-wide, in Settings → Overtime. This switch only decides whether the policy takes part.',
					],
				},
				status: {
					title: 'Default and active',
					body: [
						'Only one policy can be the default. Turning it on here takes it off whichever policy holds it now.',
						'Deactivating hides a policy from new assignments. Employees already on it keep their rules, and past attendance is untouched.',
					],
				},
			},
		}
	},
	computed: {
		policyId() {
			return this.$route.params.id ? Number(this.$route.params.id) : null
		},
		isNew() {
			return this.policyId === null
		},
		help() {
			return this.helpTopics[this.activeHelp] || this.helpTopics.intro
		},
		/** How many rules this policy actually overrides, for the header chip. */
		overrideCount() {
			return Object.keys(this.buildConfig()).length
		},
		/**
		 * Plain-English read-back of the current form. Configuration here is a diff
		 * over company defaults, which is easy to misread — spelling out the outcome
		 * is what makes it checkable before saving.
		 */
		plainSummary() {
			const f = this.form
			const lines = []

			switch (f.selfie) {
				case 'none':
					lines.push('No photo is taken — attendance is marked on location alone.')
					break
				case 'in':
					lines.push('The camera opens for a selfie when punching in, but not out.')
					break
				case 'out':
					lines.push('The camera opens for a selfie when punching out, but not in.')
					break
				case 'both':
					lines.push('A selfie is taken on both the in and the out punch.')
					break
				default:
					lines.push('Selfie capture follows the company setting.')
			}

			switch (f.qr) {
				case 'none':
					lines.push('No QR scan — attendance does not require scanning an office code.')
					break
				case 'in':
					lines.push('The office QR must be scanned when punching in.')
					break
				case 'out':
					lines.push('The office QR must be scanned when punching out.')
					break
				case 'both':
					lines.push('The office QR must be scanned on both the in and the out punch.')
					break
				default:
					lines.push('QR scanning follows the company setting.')
			}

			switch (f.shift_mode) {
				case 'none':
					lines.push('No shift. Hours are judged on total time worked, not against a start time.')
					break
				case 'fixed': {
					const found = this.shifts.find((s) => s.id === f.shift_id)
					lines.push(
						f.shift_id
							? `Everyone on this policy works the "${found ? found.name : 'selected'}" shift.`
							: "Fixed shift — but no shift is chosen, so it falls back to each employee's own shift."
					)
					break
				}
				case 'roster':
					lines.push('Shifts come from the rota assigned to each employee, day by day.')
					break
				case 'flexi':
					lines.push('Flexible hours — any time of day counts, as long as the hours add up.')
					break
				default:
					lines.push('Shift handling follows the company setting.')
			}

			const radius = f.geo_radius ? `${f.geo_radius}m` : 'the company radius'
			switch (f.geo_outside_action) {
				case 'block':
					lines.push(`Punching further than ${radius} from an allowed site is refused outright.`)
					break
				case 'allow_flagged':
					lines.push(`Punching outside ${radius} still counts, but is flagged in the register.`)
					break
				case 'allow_needs_approval':
					lines.push(`Punching outside ${radius} is held until a manager approves it.`)
					break
				default:
					lines.push('Geofencing follows the company setting.')
			}

			if (f.late_mode === 'off') {
				lines.push('Nobody is ever marked late.')
			} else if (f.late_mode === 'custom' && f.late_after_min !== null) {
				lines.push(
					f.late_after_min === 0
						? 'Arriving even a minute after the shift start counts as late.'
						: `Arriving up to ${f.late_after_min} minutes late is forgiven; after that it is a late mark.`
				)
			} else {
				lines.push('Late marking follows the company setting.')
			}

			if (f.min_hours_full) {
				lines.push(`A full day needs ${f.min_hours_full}h of work; less than that scores a half day.`)
			}

			if (f.overtime_enabled === true) {
				lines.push('Extra hours are recorded as overtime.')
			} else if (f.overtime_enabled === false) {
				lines.push('Overtime is not tracked for these employees.')
			}

			return lines
		},
	},
	mounted() {
		this.loadShifts()
		this.loadPolicies()
	},
	methods: {
		toForm(policy) {
			const config = (policy && policy.config) || {}
			// `late_after_min` carries two different "empty" meanings, so the form
			// splits them into an explicit mode: absent means inherit the company
			// value, while a stored null means late marking is deliberately switched
			// off (field staff with no shift to be late for).
			const hasLateKey = config.day_rules && 'late_after_min' in config.day_rules
			const lateMode = !hasLateKey
				? 'inherit'
				: config.day_rules.late_after_min === null
				? 'off'
				: 'custom'

			return {
				id: (policy && policy.id) || null,
				code: (policy && policy.code) || '',
				name: (policy && policy.name) || '',
				description: (policy && policy.description) || '',
				is_default: (policy && policy.is_default) || false,
				is_active: policy ? policy.is_active : true,
				raw: config,
				selfie: (config.capture && config.capture.selfie) || null,
				qr: (config.capture && config.capture.qr) || null,
				shift_mode: (config.shift && config.shift.mode) || null,
				shift_id: (config.shift && config.shift.shift_id) || null,
				geo_outside_action: (config.geo && config.geo.outside_action) || null,
				geo_radius: (config.geo && config.geo.default_radius_m) || null,
				late_mode: lateMode,
				late_after_min: lateMode === 'custom' ? config.day_rules.late_after_min : null,
				min_hours_full: (config.day_rules && config.day_rules.min_hours_full) || null,
				overtime_enabled:
					config.overtime && config.overtime.enabled !== undefined
						? config.overtime.enabled
						: null,
			}
		},
		loadShifts() {
			shiftService
				.list()
				.then((data) => {
					this.shifts = data || []
				})
				.catch(() => {})
		},
		loadPolicies() {
			if (this.isNew) {
				this.loaded = true
				return
			}
			this.loading = true
			policyService
				.list()
				.then((data) => {
					this.policies = data || []
					const found = this.policies.find((p) => p.id === this.policyId)
					if (found) {
						this.form = this.toForm(found)
						this.loaded = true
					}
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not load policy.'), type: 'error' })
				})
				.finally(() => {
					this.loading = false
				})
		},
		applyPreset(preset) {
			// Keep whatever identity the person already has when editing — only the
			// rules are replaced.
			const keepIdentity = Boolean(this.form.id)
			this.form = {
				...this.form,
				...preset.values,
				code: keepIdentity ? this.form.code : preset.values.code,
				name: keepIdentity ? this.form.name : preset.values.name,
				description: keepIdentity ? this.form.description : preset.values.description,
			}
		},
		/** Only send keys the admin actually set — a policy stores a diff, not a copy. */
		buildConfig() {
			const config = { ...this.form.raw }
			const setSection = (section, values) => {
				const kept = {}
				Object.keys(values).forEach((k) => {
					const v = values[k]
					if (v !== null && v !== '') kept[k] = v
				})
				if (Object.keys(kept).length) {
					config[section] = { ...(config[section] || {}), ...kept }
				} else {
					delete config[section]
				}
			}

			setSection('capture', { selfie: this.form.selfie, qr: this.form.qr })
			setSection('shift', { mode: this.form.shift_mode, shift_id: this.form.shift_id })
			setSection('geo', {
				outside_action: this.form.geo_outside_action,
				default_radius_m: this.form.geo_radius,
			})
			setSection('day_rules', {
				late_after_min: this.form.late_mode === 'custom' ? this.form.late_after_min : null,
				min_hours_full: this.form.min_hours_full,
			})
			setSection('overtime', { enabled: this.form.overtime_enabled })

			// "Off" is a real stored null, not an omission, so it has to be written
			// after setSection has filtered nulls out — otherwise switching late
			// marking off would silently fall back to inheriting the company's grace.
			if (this.form.late_mode === 'off') {
				config.day_rules = { ...(config.day_rules || {}), late_after_min: null }
			}

			return config
		},
		save() {
			if (!this.form.code || !this.form.code.trim() || !this.form.name || !this.form.name.trim()) {
				this.$notify({ text: 'A policy needs a code and a name.', type: 'warn' })
				return
			}
			this.saving = true
			const body = {
				code: this.form.code,
				name: this.form.name,
				description: this.form.description,
				is_default: this.form.is_default,
				is_active: this.form.is_active,
				config: this.buildConfig(),
			}
			const req = this.form.id
				? policyService.update(this.form.id, body)
				: policyService.create(body)
			req
				.then(() => {
					this.$notify({
						text: 'Saved. Recompute affected dates to apply the new rules to existing days.',
						type: 'success',
					})
					this.$router.push({ name: 'attendance-policies' })
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		goBack() {
			this.$router.push({ name: 'attendance-policies' })
		},
	},
}
</script>

<style scoped>
.preset-box {
	border: 1px solid #e5e9f2;
	border-radius: 8px;
	padding: 12px;
	margin-bottom: 12px;
	min-height: 120px;
}
.readback-panel {
	border: 1px solid #b6d4fe;
	background: #eef5ff;
}
.combine-tree {
	font-family: monospace;
	font-size: 12px;
}
</style>
